import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Copy, UserPlus } from "lucide-react";

const PLAN_OPTIONS = [
  { value: "none", label: "No plan" },
  { value: "starter", label: "Starter ($39/mo)" },
  { value: "pro", label: "Pro ($99/mo)" },
  { value: "enterprise", label: "Enterprise ($299/mo)" },
];

export default function AddUserDialog({ open, onOpenChange, onCreated }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    email: "",
    name: "",
    company: "",
    password: "",
    role: "employer",
    plan: "none",
    grant_days: "365",
  });
  const [createdInfo, setCreatedInfo] = useState(null);

  const reset = () => {
    setForm({
      email: "",
      name: "",
      company: "",
      password: "",
      role: "employer",
      plan: "none",
      grant_days: "365",
    });
    setCreatedInfo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) {
      toast.error("Email and name are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        email: form.email.trim(),
        name: form.name.trim(),
        company: form.company.trim(),
        role: form.role,
      };
      if (form.password && form.password.length >= 6) {
        payload.password = form.password;
      }
      if (form.plan && form.plan !== "none") {
        payload.plan = form.plan;
        payload.grant_days = parseInt(form.grant_days, 10) || 365;
      }
      const { data } = await api.post("/admin/users", payload);
      setCreatedInfo(data);
      onCreated?.();
      toast.success("User created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const copyCreds = async () => {
    if (!createdInfo?.generated_password) return;
    const text = `Email: ${createdInfo.email}\nTemporary password: ${createdInfo.generated_password}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Credentials copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="add-user-dialog" className="sm:max-w-md">
        {!createdInfo ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                Add user
              </DialogTitle>
              <DialogDescription>
                Create an account. Leave plan as "No plan" for a free account,
                or grant a complimentary subscription.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="add-user-email">Email *</Label>
                <Input
                  id="add-user-email"
                  data-testid="add-user-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) =>
                    setForm({ ...form, email: e.target.value })
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="add-user-name">Full name *</Label>
                <Input
                  id="add-user-name"
                  data-testid="add-user-name"
                  required
                  value={form.name}
                  onChange={(e) =>
                    setForm({ ...form, name: e.target.value })
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="add-user-company">Company</Label>
                <Input
                  id="add-user-company"
                  data-testid="add-user-company"
                  value={form.company}
                  onChange={(e) =>
                    setForm({ ...form, company: e.target.value })
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="add-user-password">
                  Password{" "}
                  <span className="text-xs text-slate-400">
                    (leave blank to auto-generate)
                  </span>
                </Label>
                <Input
                  id="add-user-password"
                  data-testid="add-user-password"
                  type="text"
                  placeholder="Min 6 characters"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label>Role</Label>
                  <Select
                    value={form.role}
                    onValueChange={(v) => setForm({ ...form, role: v })}
                  >
                    <SelectTrigger data-testid="add-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employer">Employer</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Plan</Label>
                  <Select
                    value={form.plan}
                    onValueChange={(v) => setForm({ ...form, plan: v })}
                  >
                    <SelectTrigger data-testid="add-user-plan">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLAN_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.plan !== "none" && (
                <div className="grid gap-2">
                  <Label htmlFor="add-user-days">
                    Free access duration (days)
                  </Label>
                  <Input
                    id="add-user-days"
                    data-testid="add-user-grant-days"
                    type="number"
                    min="1"
                    value={form.grant_days}
                    onChange={(e) =>
                      setForm({ ...form, grant_days: e.target.value })
                    }
                  />
                  <p className="text-xs text-slate-500">
                    The user gets full access to the selected plan for this
                    many days, with no payment required.
                  </p>
                </div>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => handleClose(false)}
                  data-testid="add-user-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="add-user-submit"
                  className="bg-brand-gradient text-white"
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  {submitting ? "Creating…" : "Create user"}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                User created
              </DialogTitle>
              <DialogDescription>
                {createdInfo.generated_password
                  ? "Share these credentials with the user. The temporary password will not be shown again."
                  : "The user has been created with the password you set."}
              </DialogDescription>
            </DialogHeader>
            <div
              data-testid="add-user-result"
              className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm"
            >
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  Email
                </div>
                <div className="font-mono">{createdInfo.email}</div>
              </div>
              {createdInfo.generated_password && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                    Temporary password
                  </div>
                  <div
                    data-testid="add-user-generated-password"
                    className="font-mono"
                  >
                    {createdInfo.generated_password}
                  </div>
                </div>
              )}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  Role · Plan
                </div>
                <div>
                  {createdInfo.role} ·{" "}
                  {createdInfo.subscription_plan || "no plan (free account)"}
                </div>
              </div>
            </div>
            <DialogFooter>
              {createdInfo.generated_password && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={copyCreds}
                  data-testid="add-user-copy"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy credentials
                </Button>
              )}
              <Button
                type="button"
                onClick={() => handleClose(false)}
                data-testid="add-user-done"
                className="bg-brand-gradient text-white"
              >
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
